import { DecimalPipe, DOCUMENT, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
    type AfterViewInit,
    Component,
    computed,
    contentChild,
    DestroyRef,
    type ElementRef,
    inject,
    input,
    model,
    type OnInit,
    output,
    Pipe,
    type PipeTransform,
    signal,
    type TemplateRef,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DeepgramClient } from '@deepgram/sdk';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { AutoFocusModule } from '@openng/optimus-ui/autofocus';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { AvatarGroupModule } from '@openng/optimus-ui/avatargroup';
import { type Button, ButtonModule } from '@openng/optimus-ui/button';
import { CarouselModule } from '@openng/optimus-ui/carousel';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { DividerModule } from '@openng/optimus-ui/divider';
import { InplaceModule } from '@openng/optimus-ui/inplace';
import { MessageModule } from '@openng/optimus-ui/message';
import { type Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TagModule } from '@openng/optimus-ui/tag';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { filter, groupBy, isNil, last } from 'lodash-es';
import moment from 'moment';
import { distinctUntilChanged, finalize, forkJoin, fromEvent, map, share, skip, tap } from 'rxjs';

import { MarkdownLazyModule } from '@app/markdown-lazy.module';
import type { PipelineAdapter } from '@app/modules/assets/models/pipeline-builder.model';
import { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import {
    PlatformSettingsIntegrationsComponent,
    PROVIDER_TYPES,
} from '@app/modules/settings/components/platform-settings/platform-settings-integrations/platform-settings-integrations.component';
import type { TestRunAdapter } from '@app/modules/testing/models/test-session.model';
import { TestRunService } from '@app/modules/testing/services/test-run.service';
import {
    CHAT_ROLE,
    type ChatAttachments,
    CONTENT_MIME_TYPE,
    FEEDBACK_TYPE,
    type InteractionAdapter,
    type InteractionContent,
} from '@app/shared/components/chat/model/chat.model';
import { CodeBlockComponent } from '@app/shared/components/code-block/code-block.component';
import { SkeletonLoaderComponent } from '@app/shared/components/skeleton/skeleton-loader.component';
import { type OBJECTS, SKELETON_TYPES } from '@app/shared/constants/shared.constant';
import { CellPrettifyPipe } from '@app/shared/pipes/cell-prettify.pipe';
import { IncludesPipe } from '@app/shared/pipes/includes.pipe';
import { LinkifyPipe } from '@app/shared/pipes/linkify.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { ScopeMarkdownStylesPipe } from '@app/shared/pipes/scopeMarkdown.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { AttachmentService } from '@app/shared/services/attachment.service';
import { ClipboardService } from '@app/shared/services/clipboard.service';
import { PlatformSettingService } from '@app/shared/services/platform-setting.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';

export enum ISSUE_TYPE {
    INACCURATE = 'Inaccurate',
    OFFENSIVE = 'Offensive',
    PRIVACY_CONCERN = 'Privacy Concern',
    IRRELEVANT = 'Irrelevant',
    OTHER = 'Other',
}

export const FEEDBACK_ISSUE_TYPES = [
    ISSUE_TYPE.INACCURATE,
    ISSUE_TYPE.OFFENSIVE,
    ISSUE_TYPE.PRIVACY_CONCERN,
    ISSUE_TYPE.IRRELEVANT,
    ISSUE_TYPE.OTHER,
];

// Define an interface for the message structure
export interface TranscriptInteraction {
    content: string;
    role: CHAT_ROLE;
    sortOrder?: number;
    context?: string;
}

export interface ChatRatingFeedbackPayload {
    interactionId: number;
    clickedRating: FEEDBACK_TYPE.LIKE | FEEDBACK_TYPE.DISLIKE;
    newRating: FEEDBACK_TYPE;
    callbacks: {
        next?: () => void;
        error?: (errorMessage: string) => void;
        final?: () => void;
    };
}

export interface ChatCommentFeedbackPayload {
    interactionId: number;
    comment: string;
    issueType: string;
    callbacks: {
        next?: () => void;
        error?: (errorMessage: string) => void;
        final?: () => void;
    };
}

@Pipe({ name: 'messageTime', standalone: true })
export class MessageTimePipe implements PipeTransform {
    static getTime(value: string, relative: boolean = true): string {
        const date = moment.utc(value).local();
        const today = moment().startOf('day');
        const yesterday = moment().subtract(1, 'days').startOf('day');
        const timeFormat = date.format('hh:mm A'); // Updated to include AM/PM

        if (relative && date.isSame(today, 'd')) {
            return `Today at ${timeFormat}`;
        }
        if (relative && date.isSame(yesterday, 'd')) {
            return `Yesterday at ${timeFormat}`;
        }
        if (relative && date.isSame(today, 'year')) {
            return `${date.format('D-MMM')} at ${timeFormat}`;
        }

        return `${date.format('D-MMM-YYYY')} at ${timeFormat}`;
    }

    transform(value: string): string {
        return value ? MessageTimePipe.getTime(value) : value;
    }
}
@Pipe({ name: 'timeTaken', standalone: true })
class TimeTakenPipe implements PipeTransform {
    transform(value: number): string {
        if (isNil(value)) return '';

        let timeTaken: string;
        if (value < 1) {
            timeTaken = '<1ms';
        } else if (value >= 1000) {
            // If the time is >1sec - then round off to the nearest second
            timeTaken = `${Math.round(value / 1000)}s`;
        } else {
            timeTaken = `${value}ms`;
        }

        return `(Generated in ${timeTaken})`;
    }
}

@Pipe({ name: 'uniqueFeedbackContributors', standalone: true })
class UniqueFeedbackContributorsPipe implements PipeTransform {
    transform(
        feedbackHistory: InteractionAdapter['feedbackHistory'] = [],
    ): Array<{ name: string; count: number }> {
        return Object.entries(
            groupBy(
                (feedbackHistory || []).filter(i => i.createdBy),
                'createdBy',
            ),
        ).map(([name, items]) => ({ name, count: items.length }));
    }
}

@Pipe({ name: 'countFeedbackComment', standalone: true })
class FeedbackCommentCountPipe implements PipeTransform {
    transform(feedbackHistory: InteractionAdapter['feedbackHistory'] = []): number {
        if (!feedbackHistory || feedbackHistory.length === 0) {
            return 0;
        }

        // Filter feedback history items where rating is null and count them
        return filter(feedbackHistory, item => !isNil(item.comment)).length;
    }
}

@Component({
    selector: 'cor-chat',
    templateUrl: './chat.component.html',
    imports: [
        FormsModule,
        TooltipModule,
        IncludesPipe,
        NameBadgePipe,
        MessageTimePipe,
        TimeTakenPipe,
        UniqueFeedbackContributorsPipe,
        FeedbackCommentCountPipe,
        AccordionModule,
        MessageModule,
        ButtonModule,
        SelectButtonModule,
        CarouselModule,
        DividerModule,
        DialogModule,
        CodeBlockComponent,
        UniqueRgbColorPipe,
        MarkdownLazyModule,
        AutoFocusModule,
        PlatformSettingsIntegrationsComponent,
        TagModule,
        PopoverModule,
        CellPrettifyPipe,
        LinkifyPipe,
        ScopeMarkdownStylesPipe,
        InplaceModule,
        SkeletonLoaderComponent,
        TextareaModule,
        AvatarModule,
        DecimalPipe,
        NgClass,
        NgStyle,
        NgTemplateOutlet,
        AvatarGroupModule,
    ],
})
export class ChatComponent implements OnInit, AfterViewInit {
    /** This component is used in 3 situations:
     *  1. A chat on the test-session (via feedback-portal) -> This will have a testSessionId() && testRun()
     *  2. A chat from pipeline-chat (via pipeline page) -> This will only have testRun()
     *  3. A chat in annotation queue (to view a chat transcript better) -> This will only have testRun(), writeAccess based on user permissions of ATQ
     */

    readonly testSessionId = input<number>();
    readonly testRun = input.required<TestRunAdapter>();
    readonly interactions = model.required<InteractionAdapter[]>(); //FIXME: We should get this from testRun()
    readonly writeAccess = input.required<boolean>();
    readonly debugMode = input<boolean>(false);
    readonly isAutoChat = input<boolean>(false);

    // Pipeline info used to create / modify interactions
    readonly pipeline = input<PipelineAdapter>();
    readonly newMessageLoading = input<CHAT_ROLE.ASSISTANT | CHAT_ROLE.USER>(null);
    readonly pipelineAssets =
        input<{ entityId: number; entityName: string; objectType: OBJECTS; link: string }[]>();

    protected readonly dedupedInteractions = computed(() => {
        const deduped: InteractionAdapter[] = [];

        this.interactions().forEach((interaction: InteractionAdapter) => {
            const lastComment = (interaction.feedbackHistory || [])
                .filter(x => !!x.comment)
                ?.at(-1);
            const updatedInteraction = {
                ...interaction,
                comment: lastComment?.comment, // FIXME: Temporary field until we have a better UX for commenting
            };

            if (updatedInteraction.role === CHAT_ROLE.USER && !updatedInteraction.name) {
                updatedInteraction.name =
                    this.testRun()?.personName || updatedInteraction.createdBy;
                updatedInteraction.nameTooltip = this.testRun()?.personDescription || '';
            }

            if (updatedInteraction.role === CHAT_ROLE.ASSISTANT && !updatedInteraction.name) {
                updatedInteraction.name = 'Assistant';
            }

            const textContent = interaction.content?.find(x => x.type === 'text')?.text;
            if (
                interaction.role !== CHAT_ROLE.INTERNAL ||
                textContent === 'This session has ended'
            ) {
                deduped.push(updatedInteraction);
            } else {
                if (deduped.length === 0 || last(deduped)?.role !== CHAT_ROLE.INTERNAL) {
                    deduped.push(updatedInteraction);
                } else {
                    last(deduped).content = [
                        {
                            type: 'text',
                            text: 'The pipeline or its dependencies used in this session were modified and may affect responses.',
                        },
                    ];
                }
            }
        });

        return deduped;
    });
    /**
     * Triggers when user provides string value to prompt and click on Send or Hits Enter
     */
    sendPromptEvent = output<InteractionContent>();
    deleteInteractionEvent = output<boolean>();
    /**
     * Triggers when user ends a session OR when they want to review the session
     */
    reviewSession = output();
    /**
     * Triggers when user add's any feedback and it's handeled out of this component
     */
    ratingFeedback = output<ChatRatingFeedbackPayload>();
    commentFeedback = output<ChatCommentFeedbackPayload>();

    private readonly chatBottomAnchor = viewChild<ElementRef>('chatBottomAnchor');
    protected readonly quickStartTemplate = contentChild<TemplateRef<any>>('quickStartTemplate');
    protected readonly personaSelectionTemplate = contentChild<TemplateRef<any>>(
        'personaSelectionTemplate',
    );
    protected readonly autoChatPromptTemplate =
        contentChild<TemplateRef<any>>('autoChatPromptTemplate');
    protected readonly contentTemplate = contentChild<TemplateRef<any>>('contentTemplate'); // Any info or content we want to display at the end of the chat

    protected readonly clipboardService = inject(ClipboardService);
    protected readonly attachmentService = inject(AttachmentService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly testRunService = inject(TestRunService);
    private readonly toast = inject(ToastNotificationService);
    private readonly platformSettingService = inject(PlatformSettingService);
    private readonly pipelineService = inject(PipelineService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly document = inject(DOCUMENT);

    protected FEEDBACK_TYPE = FEEDBACK_TYPE;
    protected readonly CHAT_ROLE = CHAT_ROLE;
    protected CONTENT_MIME_TYPE = CONTENT_MIME_TYPE;
    protected readonly PROVIDER_TYPES = PROVIDER_TYPES;
    protected readonly FEEDBACK_ISSUE_TYPES = FEEDBACK_ISSUE_TYPES;
    protected readonly ISSUE_TYPE = ISSUE_TYPE;
    protected readonly SKELETON_TYPES = SKELETON_TYPES;

    protected readonly showScrollButton = signal(false);
    protected readonly highlightedMessageId = signal<number | null>(null);

    protected readonly imagePreview = {
        url: signal<string>(null),
        isVisible: signal(false),
        open: (url: string) => {
            this.imagePreview.url.set(url);
            this.imagePreview.isVisible.set(true);
        },
        close: () => {
            this.imagePreview.isVisible.set(false);
            this.imagePreview.url.set(null);
        },
    };

    protected composer = {
        userMessage: signal(''),
        // File related functions and data:
        attachments: signal<ChatAttachments[]>([]),
        isUploading: signal(false),
        onFileSelect: (event: Event) => {
            this.composer.uploadFiles((event?.target as HTMLInputElement)?.files ?? []);
        },
        onPaste: (event: ClipboardEvent) => {
            const files = Array.from(event?.clipboardData?.files ?? []).filter(file =>
                file.type.startsWith('image/'),
            );
            if (files.length === 0) return;

            event.preventDefault();
            this.composer.uploadFiles(files);
        },
        uploadFiles: (selectedFiles: FileList | File[]) => {
            const files = Array.from(selectedFiles);
            if (files.length === 0) return;

            this.composer.isUploading.set(true);
            forkJoin(
                files.map(file =>
                    this.attachmentService.uploadAttachment({ upload: file }).pipe(
                        tap(({ result }) => {
                            this.composer.attachments.update(items => [
                                ...items,
                                {
                                    id: result.id,
                                    name: result.name,
                                    type: result.type,
                                    url: this.attachmentService.getDownloadUrl(result.id),
                                },
                            ]);
                        }),
                    ),
                ),
            )
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe()
                .add(() => this.composer.isUploading.set(false));
        },
        removeFile: (file: ChatAttachments) => {
            this.composer.attachments.update(items => items.filter(item => item.id !== file.id));
        },
    };

    protected toolCallPopover = {
        details: signal<{
            request: ArrayItem<InteractionAdapter['tool_calls']>;
            response?: InteractionAdapter;
        }>(null),
        showPopover: (
            tool: ArrayItem<InteractionAdapter['tool_calls']>,
            event: Event,
            toolCallPopoverElement: Popover,
        ) => {
            const toolResponse = this.interactions().find(
                interaction =>
                    interaction.role === CHAT_ROLE.TOOL && interaction.tool_call_id === tool.id,
            );
            this.toolCallPopover.details.set({ request: tool, response: toolResponse });
            toolCallPopoverElement.show(event);
        },
    };

    // NOTE: We allow copying interactions without an interactionId (i.e. not yet saved in the API/DB)
    //       Hence we use index and not interactionId here
    protected readonly copiedInfo = signal<{
        index: number | null;
        checked: boolean;
        type: 'message' | 'link';
    }>({ index: null, checked: false, type: null });

    protected readonly interactionState = signal(
        new Map<
            number,
            {
                isLikeSaving?: boolean;
                isDislikeSaving?: boolean;
                isAddCommentVisible?: boolean;
                isCommentSaving?: boolean;
                visibleActivityCount?: number;
                isErrorExpanded?: boolean;
                isContextExpanded?: boolean;
                isLogsExpanded?: boolean;
            }
        >(),
    );

    // pinningMessageInfo is only used in the HTML to show the pin loading spinner
    protected readonly pinningMessageInfo = signal<{
        interactionId: number;
        isSubmitting: boolean;
    }>({
        interactionId: null,
        isSubmitting: false,
    });

    protected voiceChat = {
        deepgramAPIKey: signal(''),
        isRecording: signal(false),
        isLoading: signal(false),
        mediaRecorder: signal<MediaRecorder>(null),
    };

    protected integrationsPanel = {
        showDialog: signal(false),
    };

    constructor() {
        const interactionsLength$ = toObservable(this.interactions).pipe(
            map(i => i?.length ?? 0),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef),
            share(),
        );

        // Priority: hash scroll on every interactions change (including initial load)
        interactionsLength$.subscribe(() =>
            setTimeout(() => {
                this.checkHashAndScroll();
            }, 5),
        );

        // Scroll to bottom only when a *new* message is added (skip initial); initial scroll is in ngAfterViewInit
        interactionsLength$.pipe(skip(1)).subscribe(() => {
            requestAnimationFrame(() => this.scrollToBottom(true));
        });
    }

    ngOnInit() {
        this.setupHashListener();
    }

    ngAfterViewInit(): void {
        // Single immediate scroll to bottom on load so user never sees top-first then jump
        this.scrollToBottom(false);
        this.setupScrollButtonObserver();
    }

    scrollToBottom(animate: boolean = true): void {
        try {
            this.chatBottomAnchor()?.nativeElement.scrollIntoView({
                behavior: animate ? 'smooth' : 'instant',
                block: 'start',
                inline: 'nearest',
            });
        } catch (error) {
            console.error('scrollToMessage error:', error);
        }
    }

    /** Uses IntersectionObserver so scroll-to-bottom visibility works when scroll is on body or drawer. */
    private setupScrollButtonObserver(): void {
        const anchor = this.chatBottomAnchor()?.nativeElement;
        if (!anchor) return;

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];
                if (!entry) return;
                this.showScrollButton.set(!entry.isIntersecting);
            },
            {
                root: null,
                threshold: 0,
            },
        );
        observer.observe(anchor);
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    protected pinMessage(interaction: InteractionAdapter) {
        this.pinningMessageInfo.set({
            interactionId: interaction.id,
            isSubmitting: true,
        });

        this.pipelineService
            .pinMessage(this.pipeline().id, {
                content: interaction.content,
                context: interaction.context,
                personName: this.testRun().personName,
                personDescription: this.testRun().personDescription,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe()
            .add(() => {
                this.pinningMessageInfo.set({
                    interactionId: null,
                    isSubmitting: false,
                });
            });
    }

    updateInteractionState(
        interactionId: number,
        newState: {
            isLikeSaving?: boolean;
            isDislikeSaving?: boolean;
            isAddCommentVisible?: boolean;
            isCommentSaving?: boolean;
            visibleActivityCount?: number;
            isErrorExpanded?: boolean;
            isContextExpanded?: boolean;
            isLogsExpanded?: boolean;
        },
    ) {
        this.interactionState.update(map => {
            map.set(interactionId, {
                ...(map.get(interactionId) || {}),
                ...newState,
            });
            return map;
        });
    }

    /**
     *
     * @param interactionId The interaction ID to update the rating for
     * @param clickedRating Whether the user clicked on LIKE or DISLIKE
     * @param newRating The current rating to set (could also be null to unset rating)
     */
    protected postRatingFeedback(
        interactionId: number,
        clickedRating: FEEDBACK_TYPE.LIKE | FEEDBACK_TYPE.DISLIKE,
        newRating: FEEDBACK_TYPE,
    ) {
        if (clickedRating === FEEDBACK_TYPE.LIKE) {
            this.updateInteractionState(interactionId, { isLikeSaving: true });
        } else if (clickedRating === FEEDBACK_TYPE.DISLIKE) {
            this.updateInteractionState(interactionId, { isDislikeSaving: true });
        }
        // If the user selected a dislive, we want to prompt them to put in a comment
        if (newRating === FEEDBACK_TYPE.DISLIKE) {
            this.updateInteractionState(interactionId, { isAddCommentVisible: true });
        }

        const callbacks = {
            final: () => {
                if (clickedRating === FEEDBACK_TYPE.LIKE) {
                    this.updateInteractionState(interactionId, { isLikeSaving: false });
                } else if (clickedRating === FEEDBACK_TYPE.DISLIKE) {
                    this.updateInteractionState(interactionId, { isDislikeSaving: false });
                }
            },
        };

        this.ratingFeedback.emit({ interactionId, clickedRating, newRating, callbacks });

        if (isNil(this.testRun()?.id)) return;
        this.testRunService
            .updateInteractionRating(interactionId, newRating)
            .subscribe(response => {
                // Replaces the interaction object with the response
                this.interactions.update(items =>
                    items.map(item => (item.id === interactionId ? response.result : item)),
                );
            })
            .add(() => callbacks.final());
    }

    protected postCommentFeedback(interactionId: number, comment: string, issueType: string) {
        this.updateInteractionState(interactionId, { isCommentSaving: true });
        const callbacks = {
            next: () => this.updateInteractionState(interactionId, { isAddCommentVisible: false }),
            final: () => this.updateInteractionState(interactionId, { isCommentSaving: false }),
        };
        this.commentFeedback.emit({ interactionId, comment, issueType, callbacks });
        if (isNil(this.testRun()?.id)) return;
        this.testRunService
            .addInteractionComment(interactionId, comment, issueType)
            .subscribe(response => {
                // Replaces the interaction object with the response
                this.interactions.update(items =>
                    items.map(item =>
                        // FIXME: We temporarily add "comment" here until we have a better UX for commenting
                        item.id === interactionId ? { ...response.result, comment } : item,
                    ),
                );
                callbacks.next?.();
            })
            .add(() => {
                callbacks.final?.();
            });
    }

    /**
     * Calls the delete api to delete the interaction and remove from the current object in
     * the interactions array
     */
    protected deleteMessage(interactionId: number, event: Event) {
        // Prevent navigation to details page
        event.preventDefault();
        event.stopImmediatePropagation();

        // Let user know that all non-internal messages will be deleted
        const numDeleting =
            this.interactions().filter(i => i.role !== CHAT_ROLE.INTERNAL).length -
            this.interactions()
                .filter(i => i.role !== CHAT_ROLE.INTERNAL)
                .findIndex(i => i.id === interactionId);
        const message =
            numDeleting === 1
                ? 'Are you sure you want to delete this message?'
                : `Are you sure you want to delete the ${numDeleting} messages below this?`;

        // Show confirmation before deletion
        this.confirmationService.confirm({
            key: 'confirm-popup',
            message,
            target: (event.target as any).parentElement.parentElement,
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                severity: 'secondary',
                outlined: true,
                size: 'small',
                styleClass: 'rounded',
            },
            acceptButtonProps: {
                severity: 'danger',
                size: 'small',
                styleClass: 'rounded',
            },
            accept: () => {
                if (interactionId && this.testRun()?.id) {
                    this.testRunService.deleteInteraction(interactionId).subscribe(() => {
                        this.toast.showSuccess('Chat message deleted successfully');
                        const deletedInteraction = this.interactions().find(
                            interaction => interaction.id === interactionId,
                        );
                        this.interactions.update(items =>
                            items.filter(
                                item =>
                                    item.sortOrder < deletedInteraction.sortOrder &&
                                    item.role !== CHAT_ROLE.INTERNAL,
                            ),
                        );

                        this.deleteInteractionEvent.emit(true);
                    });
                }
            },
        });
    }

    emitPrompt(event?: Event) {
        this.clearHash(); // Clear hash when sending a new message

        const content: InteractionContent = [];
        if (this.composer.userMessage()) {
            content.push({ type: 'text', text: this.composer.userMessage() });
        }

        this.composer.attachments().forEach(attachment => {
            if (!attachment.id) return;

            if (attachment.type?.startsWith('image/')) {
                content.push({ type: 'image_file', image_file: { file_id: attachment.id } });
                return;
            }
            content.push({ type: 'file', file: { file_id: attachment.id } });
        });

        if (content.length === 0) return;

        this.sendPromptEvent.emit(content);
        this.composer.userMessage.set(null);
        this.composer.attachments.set([]);

        // Need to use $event.preventDefault() for enter key otherwise an extra \n will be added
        event?.preventDefault();
    }

    protected copyCode(content: InteractionContent, index: number) {
        // FIMXE: We only copy the text content - not the images etc. for now
        const textContent = content.find(x => x.type === 'text')?.text;
        this.clipboardService.copyToClipboard(textContent).then(() => {
            // Update the copiedInfo object with the current item's index and set checked to true
            this.copiedInfo.set({ index, checked: true, type: 'message' });

            setTimeout(() => {
                this.copiedInfo.set({ index: null, checked: false, type: null });
            }, 2000);
        });
    }

    protected downloadTxtFile(content: string, filename: string = 'error.txt'): void {
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);

        // biome-ignore lint/style/noRestrictedGlobals: Should change this to use angular DOCUMENT
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        anchor.remove();
    }

    protected sendQuickButtonMessage(userMessage: string) {
        this.clearHash();
        this.sendPromptEvent.emit([{ type: 'text', text: userMessage }]);
    }

    protected hideIntegrationProvider() {
        this.integrationsPanel.showDialog.set(false);
        this.getDeepgramIntegration().subscribe();
    }

    private getDeepgramIntegration() {
        this.voiceChat.isLoading.set(true);
        return this.platformSettingService.getIntegrationByName('deepgram').pipe(
            takeUntilDestroyed(this.destroyRef),
            map(({ result }) => this.voiceChat.deepgramAPIKey.set(result?.configs?.apiKey)),
            finalize(() => this.voiceChat.isLoading.set(false)),
        );
    }

    protected async startRecording(voiceBtn: Button): Promise<void> {
        if (!this.voiceChat.deepgramAPIKey()) {
            this.getDeepgramIntegration().subscribe(() => {
                this.confirmationService.confirm({
                    key: 'confirm-popup',
                    target: voiceBtn.el.nativeElement,
                    message:
                        'Deepgram integration is required for this feature. Please configure your Deepgram API Key to continue with voice mode',
                    icon: 'pi pi-exclamation-triangle',
                    rejectButtonProps: { label: 'Cancel', severity: 'secondary', outlined: true },
                    acceptButtonProps: { label: 'Connect' },
                    accept: () => this.integrationsPanel.showDialog.set(true),
                });
            });
            return;
        }

        this.voiceChat.isLoading.set(true);
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (err) {
            this.toast.showError('Microphone access denied or unavailable.');
            console.error('Voice input error:', err);
            this.voiceChat.isLoading.set(false);
        }

        // Connecting to deepgram - Refer: https://developers.deepgram.com/docs/live-streaming-audio
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' }); // use webm for streaming
        this.voiceChat.mediaRecorder.set(mediaRecorder);

        // FIXME: Need to get the correct API Key here
        const apiKey = this.voiceChat.deepgramAPIKey();
        const deepgramClient = new DeepgramClient({ apiKey });

        const deepgramConnection = await deepgramClient.listen.v1.connect({
            model: 'nova-3', // FIXME: We should allow changing the model, language, other formats some where
            language: 'en-US',
            smart_format: 'true',
            Authorization: apiKey,
        });

        // Connect to deepgram
        deepgramConnection.on('open', () => {
            mediaRecorder.start(250); // emit every 250ms
            this.voiceChat.isRecording.set(true);
            this.voiceChat.isLoading.set(false);

            // Send the audio
            mediaRecorder.ondataavailable = mediaEv => {
                if (mediaEv.data.size > 0) {
                    mediaEv.data.arrayBuffer().then(buffer => deepgramConnection.sendMedia(buffer));
                }
            };

            // Close the deepgramConnection when media recorder stops
            mediaRecorder.onstop = () => {
                this.voiceChat.isRecording.set(false);
                deepgramConnection.close();
            };
        });

        // Get the transcript
        deepgramConnection.on('message', (data: any) => {
            if (data?.type !== 'Results') return;
            const transcript = data?.channel?.alternatives?.[0]?.transcript;
            if (!transcript?.trim()) return;

            this.composer.userMessage.update(current =>
                current?.trim() ? `${current} ${transcript}` : transcript,
            );
        });

        deepgramConnection.on('error', (err: unknown) => {
            if (this.voiceChat.isRecording()) {
                this.toast.showError('Error occured while transcribing voice.');
            } else {
                this.toast.showError('Unable to establish a connection to Deepgram.');
            }
            console.error(err);
            this.voiceChat.isRecording.set(false);
            this.voiceChat.isLoading.set(false);
        });

        deepgramConnection.connect();
        await deepgramConnection.waitForOpen();
    }

    protected stopRecording(): void {
        const mediaRecorder = this.voiceChat.mediaRecorder();
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop(); // We are stopping the media recorder
        }

        // Stop all tracks to release microphone
        mediaRecorder?.stream?.getTracks().forEach(track => {
            track.stop();
        });

        this.voiceChat.isRecording.set(false);
    }

    /**
     * Sets up a listener for hash changes in the URL
     */
    private setupHashListener() {
        // Listen to hash changes
        fromEvent(window, 'hashchange')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.checkHashAndScroll();
            });
    }

    /**
     * Checks the current hash in the URL and scrolls to the corresponding message
     */
    private checkHashAndScroll() {
        const hash = window.location.hash;
        if (!hash?.startsWith('#message-')) {
            return;
        }

        const messageId = Number.parseInt(hash.replace('#message-', ''), 10);
        if (!messageId) {
            return;
        }
        this.scrollToMessage(messageId);
    }

    /**
     * Scrolls to a specific message by ID and highlights it
     */
    private scrollToMessage(messageId: number) {
        const messageElement = this.document.getElementById(`message-${messageId}`);
        if (!messageElement) {
            // Message might not be rendered yet, try again after interactions update
            return;
        }

        try {
            // Use scrollIntoView which works better with flex-direction: column-reverse
            messageElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
            });

            // Highlight the message
            this.highlightedMessageId.set(messageId);
            setTimeout(() => {
                this.highlightedMessageId.set(null);
            }, 3000); // Remove highlight after 3 seconds
        } catch (error) {
            console.error('scrollToMessage error:', error);
        }
    }

    /**
     * Copies the deep link to a specific message to the clipboard
     */
    protected copyMessageLink(interactionId: number, event: Event) {
        event.preventDefault();
        event.stopPropagation();

        const currentUrl = window.location.href.split('#')[0];
        const messageLink = `${currentUrl}#message-${interactionId}`;

        this.clipboardService.copyToClipboard(messageLink).then(() => {
            // Update the copiedInfo object with the current item's index and set checked to true
            this.copiedInfo.set({ index: interactionId, checked: true, type: 'link' });

            setTimeout(() => {
                this.copiedInfo.set({ index: null, checked: false, type: null });
            }, 2000);
        });
    }

    /**
     * Clears the hash from the URL without triggering a hashchange event
     * FIXME: This was added to remove the hash id from url in test session.
     * Eg: If user opens http://localhost:4200/testing/test-session/41?runId=1#message-356
     * then they type a new message the scroll to bottom was not working. So we remove the hash #message-356 to
     * make it work correctly.
     */
    private clearHash() {
        if (window.location.hash) {
            // Use replaceState to remove hash without triggering hashchange event
            const url = new URL(window.location.href);
            url.hash = '';
            window.history.replaceState(null, '', url.toString());
        }
    }
}
